import { useContext, useEffect, useState } from 'react';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  DownOutlined,
} from '@ant-design/icons';
import { Collapse, Descriptions, Divider, Flex, theme } from 'antd';
import { MainContext } from '../../../../../context/MainContext';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { getConcreteClassesStatistical } from '../../../../../services/api/concreteClasses';
import styles from './index.module.scss';
import AccionaButton from '../../../../../components/AccionaButton';
import { useIntl } from 'react-intl';
import {
  getNonConformityBatch,
  getTables,
  hasNonConformity,
  getBackgroundColorByCompliance,
  COMPLIANCE_COLORS,
} from '../shared/utils.index';
import StrengthTestTable from './components/StrengthTestTable';
import PropTypes from 'prop-types';

const { actionsWrapper } = styles;

const CustomStatisticalControlBatchDetail = ({ normative }) => {
  const intl = useIntl();
  const { setLoading } = useContext(MainContext);
  const { project } = useOutletContext();
  const { batch, id, caseNumber, class: concreteClassName } = useParams();
  const navigate = useNavigate();
  const [concreteBatch, setConcreteBatch] = useState();
  const [concreteClass, setConcreteClass] = useState();
  const { token } = theme.useToken();
  const [tables, setTables] = useState();

  const getIcon = (value) => {
    if (value === true || value === 1)
      return <CheckCircleFilled style={{ color: token.colorSuccess }} />;

    if (value === false || value === 0)
      return <CloseCircleFilled style={{ color: token.colorError }} />;

    return (
      <span style={{ color: 'rgb(102, 102, 102)' }}>
        {intl
          .formatMessage({
            id: 'pages.quality.normative.statistical-control.batchDetail.notEvaluated',
            defaultMessage: 'NO EVALUADO',
          })
          .toLocaleUpperCase()}
      </span>
    );
  };
  const shouldShowCreateNC = () => {
    return (
      concreteBatch?.delivery_note_data.length > 0 &&
      concreteBatch?.min_n_control_mash <= concreteBatch.n_control_mash &&
      (concreteBatch.compliance90 === 0 ||
        concreteBatch.compliance === false) &&
      concreteBatch.non_conformity === null
    );
  };

  const getStyle = (value, value90) => {
    return {
      backgroundColor: getBackgroundColorByCompliance(value, value90),
      color: 'black',
    };
  };

  const getComplianceCell = ({ compliance, compliance90, caseNumber }) => {
    const isCase1 = caseNumber === '1';
    const isCase0 = caseNumber === '0';
    const isCeDual = isCase1 || isCase0;

    if (!isCeDual) {
      return {
        style: getStyle(compliance),
        content: getIcon(compliance),
      };
    }

    return {
      style: getStyle(compliance, compliance90),
      content: (
        <>
          {getIcon(compliance)} /{' '}
          {getIcon(compliance === null ? null : compliance90)}
        </>
      ),
    };
  };

  const getDetailedLocations = () => {
    const uniqueLocations = [
      ...new Set(
        concreteBatch?.delivery_note_data
          ?.map((item) => item?.detailed_location)
          .filter((location) => !!location),
      ),
    ];

    return uniqueLocations.join(' || ').toUpperCase();
  };

  const getMediaLabel = () => {
    if (normative === 'coe') {
      if (caseNumber === '1') {
        return 'x̅:';
      }
      if (caseNumber === '0') {
        return (
          <>
            x<sub>1</sub>:
          </>
        );
      }
    }
    if (caseNumber === '1') return '';
    if (caseNumber === '2') return 'x̅:';
    if (caseNumber === '3')
      return (
        <>
          x<sub>1</sub>:
        </>
      );
    return 'x̅:';
  };

  const getMediaValue = () => {
    if (normative === 'coe') {
      if (caseNumber === '1') {
        return (
          concreteBatch?.ave_mash_strength?.toFixed(2) ||
          concreteBatch?.min_ave_mash_strength?.toFixed(2)
        );
      }
      if (caseNumber === '0') {
        return concreteBatch?.min_ave_mash_strength?.toFixed(2);
      }
    }
    if (caseNumber === '1') return '';
    if (caseNumber === '3')
      return concreteBatch?.min_ave_mash_strength?.toFixed(2);
    return (
      concreteBatch?.ave_mash_strength?.toFixed(2) ||
      concreteBatch?.min_ave_mash_strength?.toFixed(2)
    );
  };

  const getVariabilityLabel = () => {
    if (normative === 'coe') {
      if (caseNumber === '3' || caseNumber === '0') {
        return '';
      }
      if (caseNumber === '2') {
        return (
          <>
            S<sub>35</sub>
          </>
        );
      }
    }
    if (caseNumber === '1') return '';
    if (caseNumber === '2')
      return (
        <>
          r<sub>n</sub>
        </>
      );
    if (caseNumber === '3')
      return (
        <>
          S<sub>35</sub>
        </>
      );
    return (
      <>
        r<sub>n</sub>
      </>
    );
  };

  const getVariabilityValue = () => {
    if (normative === 'coe') {
      if (caseNumber === '3' || caseNumber === '0') {
        return '';
      }
      if (caseNumber === '2') {
        return concreteBatch?.s35_mash?.toFixed(2);
      }
      if (caseNumber === '0') {
        return '';
      }
    }
    if (caseNumber === '1') return '';
    if (caseNumber === '3') return concreteBatch?.s35_mash?.toFixed(2);
    return concreteBatch?.rn_mash?.toFixed(2);
  };

  const getCoefficientLabel = () => {
    if (normative === 'coe') {
      if (caseNumber === '3') {
        return (
          <>
            K<sub>n</sub>
          </>
        );
      }
      if (caseNumber === '2') {
        return '';
      }
      if (caseNumber === '1') {
        return 'δ';
      }
      if (caseNumber === '0') {
        return '';
      }
    }
    if (caseNumber === '1') return '';
    if (caseNumber === '2')
      return (
        <>
          K<sub>2</sub>
        </>
      );
    if (caseNumber === '3')
      return (
        <>
          K<sub>3</sub>
        </>
      );
    return (
      <>
        K<sub>2</sub>:
      </>
    );
  };

  const getCoefficientValue = () => {
    if (normative === 'coe') {
      if (caseNumber === '3') {
        return concreteBatch?.kn_mash?.toFixed(2);
      }
      if (caseNumber === '2') {
        return '';
      }
      if (caseNumber === '1') {
        return concreteBatch?.delta;
      }
      if (caseNumber === '0') {
        return '';
      }
    }
    if (caseNumber === '1') return '';
    if (caseNumber === '3') return concreteBatch?.k3_mash?.toFixed(2);
    return concreteBatch?.k2_mash?.toFixed(2);
  };

  const getAcceptanceValueLabel = () => {
    // Para EHE caso 1, mostrar X₁
    if (normative !== 'coe' && caseNumber === '1') {
      return (
        <>
          X<sub>1</sub>:
        </>
      );
    }
    // Para COE caso 1, mostrar ∫(x̅)
    if (normative === 'coe') {
      if (caseNumber === '1') {
        return '∫(x̅):';
      }
      if (caseNumber === '0') {
        return '';
      }
    }
    if (caseNumber === '2') return '∫(x̅):';
    if (caseNumber === '3')
      return (
        <>
          ∫(x<sub>1</sub>):
        </>
      );
    return '∫(x̅):';
  };

  const getAcceptanceValue = () => {
    // Para EHE caso 1, mostrar fcreal (X₁)
    if (normative !== 'coe' && caseNumber === '1') {
      return concreteBatch?.fcreal?.toFixed(2);
    }
    if (normative === 'coe') {
      if (caseNumber === '0') {
        return '';
      }
    }
    // Para COE caso 1 y otros casos, mostrar acceptance_value
    return concreteBatch?.acceptance_value?.toFixed(2);
  };

  const getCriteria = () => {
    if (normative === 'ehe') {
      if (caseNumber === '1') {
        return (
          <>
            X<sub>1</sub> ≥ ∫ck
          </>
        );
      }
      if (caseNumber === '2') {
        return (
          <>
            ∫(x̅) = x̅ - K<sub>2</sub>·r<sub>n</sub> ≥ ∫ck
          </>
        );
      }
      if (caseNumber === '3') {
        return (
          <>
            ∫(x<sub>1</sub>) = x<sub>1</sub> - K<sub>3</sub>·S<sub>35</sub> ≥
            ∫ck
          </>
        );
      }
    }
    if (normative === 'coe') {
      if (caseNumber === '0') {
        return (
          <>
            x<sub>1</sub> ≥ ∫ck
          </>
        );
      }
      if (caseNumber === '1') {
        return <>∫(x̅) = x̅ · (1-1.66δ) ≥ ∫ck</>;
      }
      if (caseNumber === '2') {
        return (
          <>
            ∫(x̅ ) = x̅ - 1.66s<sub>35</sub>≥ ∫ck
          </>
        );
      }
      if (caseNumber === '3') {
        return (
          <>
            ∫(x<sub>1</sub>)=x<sub>1</sub> · K<sub>n</sub> ≥ ∫ck
          </>
        );
      }
    }
  };

  useEffect(() => {
    setLoading(true);
    const findConcreteClass = async () => {
      const data = await getConcreteClassesStatistical(id);
      if (!data) {
        navigate('../');
      } else {
        const concreteClass = data.data.data.find(
          (c) => c.name === concreteClassName,
        );
        const concreteBatch = concreteClass.concrete_batch_data
          .filter((b) => b.batch_code === parseInt(batch, 10))
          .reduce((acc, cur) => {
            cur.delivery_note_data.forEach((dn) => {
              acc.push({
                ...dn,
                batch_code: cur.batch_code,
                batch_volume: cur.batch_volume,
                posicion_orden_xi: cur.posicion_orden_xi,
                total_control_mash: cur.total_control_mash,
                fcreal: cur.fcreal,
                concrete_value: cur.concrete_value,
                compliance: cur.compliance,
                compliance90: cur.compliance90,
                non_conformity: cur.non_conformity,
                min_n_control_mash: cur.min_n_control_mash,
                n_control_mash: cur.n_control_mash,
                ave_mash_strength: cur.ave_mash_strength,
                min_ave_mash_strength: cur.min_ave_mash_strength,
                rn_mash: cur.rn_mash,
                k2_mash: cur.k2_mash,
                s35_mash: cur.s35_mash,
                k3_mash: cur.k3_mash,
                kn_mash: cur.kn_mash,
                acceptance_value: cur.acceptance_value,
                arriving_date_first: cur.arriving_date_first,
                arriving_date_last: cur.arriving_date_last,
                delivery_note_data: cur.delivery_note_data,
                result_mash: cur.result_mash,
                tag_batch_data: cur.tag_batch_data,
              });
            });
            return acc;
          }, [])[0];
        setConcreteBatch(concreteBatch);
        const tables = getTables(concreteBatch.delivery_note_data);

        setTables(tables);
        setConcreteClass(concreteClass);
        setLoading(false);
      }
    };
    findConcreteClass();
  }, [batch, caseNumber, concreteClassName, id, navigate, setLoading]);
  return (
    <>
      <div className={`row ${actionsWrapper}`}>
        <div className="col-sm-12">
          <div className={`row ${actionsWrapper}`}>
            <div className="col-sm-12">
              <Flex justify="flex-end">
                <AccionaButton
                  style={{ padding: 15, marginRight: 'auto' }}
                  id="statisticalControl"
                  type="white"
                  onClick={() => {
                    navigate(
                      `../statistical-control/lotting/${concreteClass.name}`,
                    );
                  }}
                  msg={intl.formatMessage({ id: 'back' })}
                  classIcon="icon fas fa-arrow-left"
                />
                <AccionaButton
                  id="traceability"
                  style={{ padding: 15 }}
                  type="primary"
                  onClick={() => {
                    navigate('../traceability');
                  }}
                  msg={intl.formatMessage({ id: 'button.traceability' })}
                  classIcon="icon fas fa-route"
                />
                <AccionaButton
                  style={{ padding: 15 }}
                  id="100%Control"
                  type="primary"
                  onClick={() => {
                    navigate(`../100-control`);
                  }}
                  msg={intl.formatMessage({ id: 'button.100%Control' })}
                  classIcon="icon fas fa-percentage"
                />
                <AccionaButton
                  style={{ padding: 15 }}
                  id="statisticalControl"
                  type="primary"
                  onClick={() => {
                    navigate(`../statistical-control`);
                  }}
                  msg={intl.formatMessage({ id: 'button.statisticalControl' })}
                  classIcon="icon fas fa-chart-bar"
                />
              </Flex>
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <Collapse
            defaultActiveKey={['1']}
            expandIconPosition="end"
            expandIcon={({ isActive }) => (
              <DownOutlined rotate={isActive ? 180 : 0} />
            )}
            style={{ background: 'transparent', border: 'none' }}
            items={[
              {
                key: '1',
                label: (
                  <Flex justify="flex-start" gap="8px" align="center">
                    <span>
                      {intl
                        .formatMessage({
                          id: 'pages.quality.normative.statistical-control.batchDetail.title',
                          defaultMessage: 'Control estadístico',
                        })
                        .toUpperCase()}
                    </span>
                    <Divider type="vertical" />
                    <span>
                      {intl
                        .formatMessage({
                          id: 'pages.quality.normative.statistical-control.batchDetail.batch',
                          defaultMessage: 'Lote',
                        })
                        .toUpperCase()}
                      :{' '}
                      {project.version === 1
                        ? batch
                        : `${concreteBatch?.tag_batch_data?.tag_data.name} ${concreteBatch?.tag_batch_data?.tag_number}`}
                    </span>
                    <Divider type="vertical" />
                    <span>
                      {intl
                        .formatMessage({
                          id: 'pages.quality.normative.statistical-control.batchDetail.caseNumber',
                          defaultMessage: 'Caso',
                        })
                        .toUpperCase()}
                      : {caseNumber}
                    </span>
                    <Divider type="vertical" />
                    <span>
                      {intl
                        .formatMessage({
                          id: 'pages.quality.normative.statistical-control.batchDetail.criteria',
                          defaultMessage: 'Criterio aceptado',
                        })
                        .toUpperCase()}
                      : {getCriteria()}
                    </span>
                  </Flex>
                ),
                children: (
                  <Descriptions column={4} bordered>
                    <Descriptions.Item
                      key="className"
                      label={intl
                        .formatMessage({
                          id: 'pages.quality.normative.statistical-control.batchDetail.className',
                          defaultMessage: 'Clase Hormigón Obra',
                        })
                        .toLocaleUpperCase()}
                      span={{ xl: 2, xxl: 2 }}
                      styles={{
                        label: {
                          width: '25%',
                        },
                        content: {
                          width: '25%',
                        },
                      }}
                    >
                      {concreteClass?.name}
                    </Descriptions.Item>
                    <Descriptions.Item
                      key="min_n_control_mash"
                      label={intl
                        .formatMessage({
                          id: 'pages.quality.normative.statistical-control.batchDetail.min_n_control_mash',
                          defaultMessage: 'Amasadas mínimas',
                        })
                        .toLocaleUpperCase()}
                    >
                      {concreteBatch?.min_n_control_mash}
                    </Descriptions.Item>
                    <Descriptions.Item
                      key="min_n_control_mash"
                      label={intl
                        .formatMessage({
                          id: 'pages.quality.normative.statistical-control.batchDetail.n_control_mash',
                          defaultMessage: 'Amasadas máximas',
                        })
                        .toLocaleUpperCase()}
                    >
                      {concreteBatch?.n_control_mash}
                    </Descriptions.Item>
                    <Descriptions.Item
                      key="plant"
                      label={intl
                        .formatMessage({
                          id: 'pages.quality.normative.statistical-control.batchDetail.plant',
                          defaultMessage: 'Planta',
                        })
                        .toLocaleUpperCase()}
                      span={{ xl: 2, xxl: 2 }}
                    >
                      {concreteClass?.plant}
                    </Descriptions.Item>
                    <Descriptions.Item
                      key="ave_mash_strength"
                      label={getMediaLabel()}
                      span={{ xl: 2, xxl: 2 }}
                    >
                      {getMediaValue()}
                    </Descriptions.Item>
                    <Descriptions.Item
                      key="recipe"
                      label={intl
                        .formatMessage({
                          id: 'pages.quality.normative.statistical-control.batchDetail.recipe',
                          defaultMessage: 'Tipo de Hormigón',
                        })
                        .toLocaleUpperCase()}
                      span={{ xl: 2, xxl: 2 }}
                    >
                      {concreteClass?.recipe}
                    </Descriptions.Item>
                    <Descriptions.Item
                      key="variability"
                      label={getVariabilityLabel()}
                      span={{ xl: 2, xxl: 2 }}
                    >
                      {getVariabilityValue()}
                    </Descriptions.Item>
                    <Descriptions.Item
                      key="batch_volume"
                      span={{ xl: 2, xxl: 2 }}
                      label={intl
                        .formatMessage({
                          id: 'pages.quality.normative.statistical-control.batchDetail.batch_volume',
                          defaultMessage: 'VOLUMEN LOTE [m³]',
                        })
                        .toLocaleUpperCase()}
                      contentStyle={{
                        background:
                          concreteBatch?.batch_volume_compliance === 1
                            ? COMPLIANCE_COLORS.ERROR
                            : COMPLIANCE_COLORS.TRANSPARENT,
                      }}
                    >
                      {concreteBatch?.batch_volume?.toFixed(2)}
                    </Descriptions.Item>
                    <Descriptions.Item
                      key="coefficient"
                      label={getCoefficientLabel()}
                      span={{ xl: 2, xxl: 2 }}
                    >
                      {getCoefficientValue()}
                    </Descriptions.Item>
                    <Descriptions.Item
                      key="structural_element"
                      label={intl
                        .formatMessage({
                          id: 'pages.quality.normative.statistical-control.batchDetail.structural_element',
                          defaultMessage: 'Elemento estructural',
                        })
                        .toLocaleUpperCase()}
                      span={{ xl: 2, xxl: 2 }}
                    >
                      {concreteBatch?.structural_element}
                    </Descriptions.Item>
                    <Descriptions.Item
                      key="acceptance_value"
                      label={getAcceptanceValueLabel()}
                      span={{ xl: 2, xxl: 2 }}
                    >
                      {getAcceptanceValue()}
                    </Descriptions.Item>
                    <Descriptions.Item
                      key="arriving_date_first"
                      span={{ xl: 2, xxl: 2 }}
                      label={intl
                        .formatMessage({
                          id: 'pages.quality.normative.statistical-control.batchDetail.arriving_date_first',
                          defaultMessage: 'Fecha inicio lote',
                        })
                        .toLocaleUpperCase()}
                      contentStyle={{
                        color:
                          concreteBatch?.batch_time_compliance === 1
                            ? 'red'
                            : 'rgb(102, 102, 102)',
                        background:
                          concreteBatch?.batch_time_compliance === 1
                            ? COMPLIANCE_COLORS.ERROR
                            : 'inherit',
                      }}
                    >
                      {concreteBatch?.arriving_date_first}
                    </Descriptions.Item>
                    <Descriptions.Item
                      key="concrete_value"
                      label={
                        concreteBatch?.certified_dis === true ||
                        concreteBatch?.concrete_quality === true
                          ? '∫ck / ∫ck 90%'
                          : '∫ck'
                      }
                      span={{ xl: 2, xxl: 2 }}
                    >
                      {concreteBatch?.certified_dis === true ||
                      concreteBatch?.concrete_quality === true
                        ? `${concreteBatch?.concrete_value?.toFixed(2)} / ${(
                            (concreteBatch.concrete_value || 0) * 0.9
                          ).toFixed(2)}`
                        : concreteBatch?.concrete_value?.toFixed(2)}
                    </Descriptions.Item>
                    <Descriptions.Item
                      span={{ xl: 2, xxl: 2 }}
                      key="arriving_date_last"
                      label={intl
                        .formatMessage({
                          id: 'pages.quality.normative.statistical-control.batchDetail.arriving_date_last',
                          defaultMessage: 'Fecha fin lote',
                        })
                        .toLocaleUpperCase()}
                      contentStyle={{
                        color:
                          concreteBatch?.batch_time_compliance === 1
                            ? 'red'
                            : 'rgb(102, 102, 102)',
                        background:
                          concreteBatch?.batch_time_compliance === 1
                            ? COMPLIANCE_COLORS.ERROR
                            : 'inherit',
                      }}
                    >
                      {concreteBatch?.arriving_date_last}
                    </Descriptions.Item>
                    <Descriptions.Item
                      span={{ xl: 2, xxl: 2 }}
                      key="compliance"
                      label={`${intl
                        .formatMessage({
                          id:
                            normative === 'coe'
                              ? 'pages.quality.normative.statistical-control.batchDetail.compliance.coe'
                              : 'pages.quality.normative.statistical-control.batchDetail.compliance',
                          defaultMessage:
                            normative === 'coe'
                              ? 'Cumplimiento lote resistencia [Tabla 57.5.4.3.A]:'
                              : 'Cumplimiento lote resistencia [Tabla 86.5.4.3a]:',
                        })
                        .toLocaleUpperCase()} ${
                        concreteBatch?.concrete_quality === true ||
                        concreteBatch?.certified_dis === true
                          ? '/ 90%'
                          : ''
                      }`}
                      contentStyle={
                        concreteBatch
                          ? getComplianceCell({ ...concreteBatch, caseNumber })
                              .style
                          : {}
                      }
                    >
                      {concreteBatch &&
                        getComplianceCell({ ...concreteBatch, caseNumber })
                          .content}
                    </Descriptions.Item>
                    <Descriptions.Item
                      span={{ xl: 4, xxl: 4 }}
                      key="detailed_location"
                      label={intl
                        .formatMessage({
                          id: 'pages.quality.normative.statistical-control.batchDetail.detailed_location',
                          defaultMessage: 'Situación obra',
                        })
                        .toLocaleUpperCase()}
                    >
                      {getDetailedLocations()}
                    </Descriptions.Item>
                  </Descriptions>
                ),
              },
            ]}
          />
          <Divider />
          {tables?.before3Tables.length > 0 &&
            tables?.before3Tables.map((table, index) => (
              <div key={index} style={{ marginTop: '3em' }}>
                <StrengthTestTable
                  data={table.sameDay}
                  days={table.extraDay}
                  title={intl.formatMessage(
                    {
                      id: 'pages.quality.normative.statistical-control.batchDetail.table.title_dynamic',
                      defaultMessage: 'TABLA DE ROTURA A {days} DÍAS',
                    },
                    { days: table.extraDay },
                  )}
                />
              </div>
            ))}
          {tables?.d3Table.length > 0 && (
            <div style={{ marginTop: '3em' }}>
              <StrengthTestTable
                data={tables?.d3Table}
                days={3}
                title={intl.formatMessage({
                  id: 'pages.quality.normative.statistical-control.batchDetail.table.title_3',
                  defaultMessage: 'TABLA DE ROTURA A 3 DÍAS [INFORMATIVO]',
                })}
              />
            </div>
          )}
          {tables?.before7Tables.length > 0 &&
            tables?.before7Tables.map((table, index) => (
              <div key={index} style={{ marginTop: '3em' }}>
                <StrengthTestTable
                  data={table.sameDay}
                  days={table.extraDay}
                  title={intl.formatMessage(
                    {
                      id: 'pages.quality.normative.statistical-control.batchDetail.table.title_dynamic',
                      defaultMessage:
                        'TABLA DE ROTURA A {days} DÍAS [INFORMATIVO]',
                    },
                    { days: table.extraDay },
                  )}
                />
              </div>
            ))}
          {tables?.d7Table.length > 0 && (
            <div style={{ marginTop: '3em' }}>
              <StrengthTestTable
                data={tables?.d7Table}
                days={7}
                title={intl.formatMessage({
                  id: 'pages.quality.normative.statistical-control.batchDetail.table.title_7',
                  defaultMessage: 'TABLA DE ROTURA A 7 DÍAS [INFORMATIVO]',
                })}
              />
            </div>
          )}
          {tables?.before28Tables.length > 0 &&
            tables?.before28Tables.map((table, index) => (
              <div key={index} style={{ marginTop: '3em' }}>
                <StrengthTestTable
                  data={table.sameDay}
                  days={table.extraDay}
                  title={intl.formatMessage(
                    {
                      id: 'pages.quality.normative.statistical-control.batchDetail.table.title_dynamic',
                      defaultMessage:
                        'TABLA DE ROTURA A {days} DÍAS [INFORMATIVO]',
                    },
                    { days: table.extraDay },
                  )}
                />
              </div>
            ))}
          {concreteBatch && (
            <div style={{ marginTop: '3em' }}>
              <StrengthTestTable
                data={concreteBatch.delivery_note_data}
                days={28}
                title={intl.formatMessage({
                  id: 'pages.quality.normative.statistical-control.batchDetail.table.title_28',
                  defaultMessage: 'TABLA DE ROTURA A 28 DÍAS [CUMPLIMIENTO]',
                })}
              />
            </div>
          )}
          {tables?.afterTables.length > 0 &&
            tables?.afterTables.map((table, index) => (
              <div key={index} style={{ marginTop: '3em' }}>
                <StrengthTestTable
                  data={table.sameDay}
                  days={table.extraDay}
                  title={intl.formatMessage(
                    {
                      id: 'pages.quality.normative.statistical-control.batchDetail.table.title_dynamic',
                      defaultMessage:
                        'TABLA DE ROTURA A {days} DÍAS [INFORMATIVO]',
                    },
                    { days: table.extraDay },
                  )}
                />
              </div>
            ))}
          {tables?.d60Table.length > 0 && (
            <div style={{ marginTop: '3em' }}>
              <StrengthTestTable
                data={tables?.d60Table}
                days={60}
                title={intl.formatMessage({
                  id: 'pages.quality.normative.statistical-control.batchDetail.table.title_60',
                  defaultMessage: 'TABLA DE ROTURA A 60 DÍAS [INFORMATIVO]',
                })}
              />
            </div>
          )}
        </div>
      </div>
      <Divider />
      <div className={`row ${actionsWrapper}`}>
        <div className="col-sm-12">
          <div className={`row ${actionsWrapper}`}>
            <div className="col-sm-12">
              <Flex justify="flex-end">
                <AccionaButton
                  style={{ padding: 15, marginRight: 'auto' }}
                  id="statisticalControl"
                  type="white"
                  onClick={() => {
                    navigate(
                      `../statistical-control/lotting/${concreteClass.name}`,
                    );
                  }}
                  msg={intl.formatMessage({ id: 'back' })}
                  classIcon="icon fas fa-arrow-left"
                />
                {concreteBatch && hasNonConformity([concreteBatch]) && (
                  <AccionaButton
                    id="traceability"
                    style={{ padding: 15 }}
                    type="primary"
                    onClick={() => {
                      navigate(
                        `../../ocr/nonConformity/${getNonConformityBatch([
                          concreteBatch,
                        ])}`,
                      );
                    }}
                    msg={intl.formatMessage({
                      id: 'pages.quality.normative.statistical-control.batchDetail.nonConformityList.button',
                      defaultMessage: 'Listado de NC',
                    })}
                  />
                )}
                {concreteBatch && shouldShowCreateNC() && (
                  <AccionaButton
                    style={{ padding: 15 }}
                    id="createNC"
                    type="primary"
                    onClick={() => {
                      navigate(
                        `../../ocr/nonConformity/create/${concreteBatch.concrete_batch}`,
                      );
                    }}
                    msg={intl.formatMessage({
                      id: 'pages.quality.normative.statistical-control.batchDetail.createNC.button',
                      defaultMessage: 'Crear NC',
                    })}
                  />
                )}
              </Flex>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
CustomStatisticalControlBatchDetail.propTypes = {
  normative: PropTypes.string.isRequired,
};

export default CustomStatisticalControlBatchDetail;
